import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIonRouter } from "@ionic/react";
import {
  changePassword,
  ChangePasswordRequest,
  checkEmail,
  login,
  LoginRequest,
  loginWithCode,
  loginWithTwoFactor,
  logout,
  register,
  sendLoginCode,
  sendRegistrationCode,
  TwoFactorLoginRequest,
  UserLoginResponse,
  verifyAuth,
  verifyRegistrationCode,
} from "@/api/auth";
import { pushNotificationService } from "@services/pushNotifications";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ReactNode } from "react";
import { create } from "zustand";
import { report } from "@services/Sentry.ts";
import type {
  Organisation,
  OrganisationSettings,
  RegisterRequest,
} from "@/types";
import { organisationStorage } from "@/services/storage";
import { useSwitchOrganisation } from "@/hooks/useOrganisations.ts";
import { getOrganisations } from "@/api";
import { handlePendingInvitation } from "@/hooks/usePendingInvitation";

// Auth state store
interface AuthState {
  isAuthenticated: boolean;
  user: UserLoginResponse | null;
  currentOrganisation: Organisation | null;
  organisationSettings: OrganisationSettings | null;
  setAuth: (isAuthenticated: boolean, user: UserLoginResponse | null) => void;
  setCurrentOrganisation: (org: Organisation | null) => void;
  setOrganisationSettings: (settings: OrganisationSettings | null) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  user: null,
  currentOrganisation: null,
  organisationSettings: null,
  setAuth: (isAuthenticated, user) => set({ isAuthenticated, user }),
  setCurrentOrganisation: (currentOrganisation) => set({ currentOrganisation }),
  setOrganisationSettings: (organisationSettings) =>
    set({ organisationSettings }),
  clearAuth: () =>
    set({
      isAuthenticated: false,
      user: null,
      currentOrganisation: null,
      organisationSettings: null,
    }),
}));

const AuthContext = createContext({ isLoading: true });

/**
 * Provider that runs auth initialisation exactly once on mount:
 * 1. Verify the token
 * 2. Load the user
 * 3. Load the stored organisation (or record that none exists)
 *
 * All state is written to the Zustand store so the rest of the app
 * continues to use useAuthStore / useAuth as before.
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const { setAuth, setCurrentOrganisation } = useAuthStore.getState();

    (async () => {
      try {
        const user = await verifyAuth();
        if (cancelled) return;

        if (!user) {
          setAuth(false, null);
          return;
        }

        setAuth(true, user);

        const org = await organisationStorage.getCurrentOrganisation();
        if (cancelled) return;

        setCurrentOrganisation(org ?? null);
      } catch {
        if (!cancelled) setAuth(false, null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return React.createElement(
    AuthContext.Provider,
    { value: { isLoading } },
    children
  );
};

/**
 * Hook for authentication status and user data
 */
export const useAuth = () => {
  const { isLoading } = useContext(AuthContext);
  const { isAuthenticated, user, currentOrganisation } = useAuthStore();
  return { isLoading, isAuthenticated, user, currentOrganisation };
};

/**
 * Hook for login mutation
 */
export const useLogin = () => {
  const queryClient = useQueryClient();
  const router = useIonRouter();
  const { setAuth } = useAuthStore();

  return useMutation({
    mutationFn: (request: LoginRequest) => login(request),
    onSuccess: async (data) => {
      // If 2FA is required, don't navigate - parent component will handle this
      if (data.two_factor_required) {
        return;
      }

      // Update auth state
      if (data.user) {
        setAuth(true, data.user);

        // Initialize push notifications for PWA
        pushNotificationService.initialize().catch((e: unknown) => {
          console.error(e);
          report(e);
        });

        // Invalidate and refetch auth queries
        queryClient.invalidateQueries({ queryKey: ["auth"] });

        // Navigate to tabs (main app)
        router.push("/tabs", "root", "replace");
      }
    },
  });
};

/**
 * Generic Actions for auth events
 */
export const useAuthActions = () => {
  const queryClient = useQueryClient();
  const router = useIonRouter();
  const { setAuth } = useAuthStore();
  const { mutateAsync: switchOrganisation } = useSwitchOrganisation();

  /**
   * Handle successful login actions
   */
  const onLoginSuccessAction = useCallback(
    async (loginResponse: UserLoginResponse) => {
      setAuth(true, loginResponse);

      // Initialize push notifications for PWA
      pushNotificationService.initialize().catch((e: unknown) => {
        console.error(e);
        report(e);
      });

      // Invalidate and refetch auth queries
      await queryClient.invalidateQueries({ queryKey: ["auth"] });

      // Check for deep link invitation pending from before login
      await handlePendingInvitation(queryClient);

      const { organisations } = await getOrganisations();

      // We let switchOrganisation handle redirect logic
      if (organisations.length) {
        await switchOrganisation(organisations[0].id);
        return;
      }

      // Navigate to tabs (main app)
      router.push("/tabs", "root", "replace");
    },
    [queryClient, router, setAuth, switchOrganisation]
  );

  return {
    onLoginSuccessAction,
  };
};

/**
 * Hook for 2FA login mutation
 */
export const useTwoFactorLogin = () => {
  const { onLoginSuccessAction } = useAuthActions();

  return useMutation({
    mutationFn: (request: TwoFactorLoginRequest) => loginWithTwoFactor(request),
    onSuccess: (data) => {
      // Update auth state
      if (data.user) {
        onLoginSuccessAction(data.user);
      }
    },
  });
};

/**
 * Hook for logout mutation
 */
export const useLogout = () => {
  const queryClient = useQueryClient();
  const router = useIonRouter();
  const { clearAuth } = useAuthStore();

  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      // Clear auth state
      clearAuth();

      // Clear all cached queries
      queryClient.clear();

      // Navigate to welcome
      router.push("/auth/welcome", "root", "replace");
    },
    onError: () => {
      // Even on error, clear local auth and redirect
      clearAuth();
      queryClient.clear();
      router.push("/auth/welcome", "root", "replace");
    },
  });
};

/**
 * Hook for change password mutation
 */
export const useChangePassword = () => {
  return useMutation({
    mutationFn: (request: ChangePasswordRequest) => changePassword(request),
  });
};

/**
 * Hook to check if email exists
 */
export const useCheckEmail = () => {
  return useMutation({
    mutationFn: (email: string) => checkEmail(email),
  });
};

/**
 * Hook to send registration code
 */
export const useSendRegistrationCode = () => {
  return useMutation({
    mutationFn: (email: string) => sendRegistrationCode(email),
  });
};

/**
 * Hook to verify registration code (without consuming)
 */
export const useVerifyRegistrationCode = () => {
  return useMutation({
    mutationFn: ({ email, code }: { email: string; code: string }) =>
      verifyRegistrationCode(email, code),
  });
};

/**
 * Hook for registration mutation
 */
export const useRegister = () => {
  const { onLoginSuccessAction } = useAuthActions();

  return useMutation({
    mutationFn: (request: Omit<RegisterRequest, "device_name">) =>
      register(request),
    onSuccess: (data) => {
      // Update auth state
      if (data.user) {
        onLoginSuccessAction(data.user);
      }
    },
  });
};

/**
 * Hook to send login code
 */
export const useSendLoginCode = () => {
  return useMutation({
    mutationFn: (email: string) => sendLoginCode(email),
  });
};

/**
 * Hook for login with code mutation
 */
export const useLoginWithCode = () => {
  const { onLoginSuccessAction } = useAuthActions();
  return useMutation({
    mutationFn: ({ email, code }: { email: string; code: string }) =>
      loginWithCode({ email, code }),
    onSuccess: (data) => {
      // If 2FA is required, don't navigate - parent component will handle this
      if (data.two_factor_required) {
        return;
      }

      // Update auth state
      if (data.user) {
        onLoginSuccessAction(data.user);
      }
    },
  });
};
